import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '../context/AuthContext.js'
import VisitorQueue from './VisitorQueue.jsx'
import BarrierControls from '../components/BarrierControls.jsx'
import KioskDisplayControls from '../components/KioskDisplayControls.jsx'
import './DestinationBoard.css'

// Where a personal view preference is remembered. Per browser on purpose: it is a
// convenience, not configuration — the tenant's own default lives in the database and
// is what anyone sees until they choose otherwise.
const VIEW_KEY = 'viguard.destinationView'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'
const REFRESH_MS = 15000

/** 'YYYY-MM-DD' for a Date, in local terms — the board is a calendar day, not an instant. */
const isoDay = (d) => {
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

const shiftDay = (iso, days) => {
  // Parsed at noon so a DST boundary cannot roll the date back a day.
  const d = new Date(`${iso}T12:00:00`)
  d.setDate(d.getDate() + days)
  return isoDay(d)
}

const hhmm = (iso) => {
  if (!iso) return null
  return new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
}

/** Minutes since midnight for an ISO timestamp, used to drop it into a slot. */
const minutesOfDay = (iso) => {
  const d = new Date(iso)
  return d.getHours() * 60 + d.getMinutes()
}

/**
 * Past its time limit and not yet dealt with.
 *
 * Computed client-side from expected_until so the row turns amber the moment it is due,
 * rather than waiting for the server's scan — the scan's job is to ALERT once, this is
 * just the colour. An assignment with no timer is never overdue.
 */
const isOverdue = (v) =>
  !!v.expectedUntil && new Date(v.expectedUntil).getTime() < Date.now()

/** 'HH:MM' from the standby configuration to minutes since midnight. */
const hhmmToMinutes = (value) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(value ?? '')
  return m ? Number(m[1]) * 60 + Number(m[2]) : 0
}

/**
 * The occupancy board — the single surface for "where does this visitor go".
 *
 * Three things on one screen, because they are one job:
 *   • who still needs an answer (including visitors mid-flow, who are the ones worth
 *     answering early while it can still reach the terminal)
 *   • the day grid: destinations across the top, time slots down the side
 *   • who is currently on site and where they were sent, kept until checkout
 *
 * This replaced a separate "Besucher im Ablauf" page. That page and this one both
 * listed the same visitors and both offered a way to say where they should go — one
 * assigning an info page, the other a destination — so the same driver appeared twice
 * with two controls for one decision, and could end up with two different answers.
 *
 * A visitor now appears exactly once. Where their flow allows a bare info page to be
 * assigned, that control sits on their row next to the grid rather than on its own
 * page; a destination carries its own info page, so the two are alternatives, never
 * both.
 *
 * Open to every dashboard role including `user`: assigning and releasing is reception's
 * daily work, not configuration.
 */
export default function DestinationBoard() {
  const { admin, isOwner, apiFetch, getToken, account } = useAuth()

  const [tenants,  setTenants]  = useState([])
  const [tenantId, setTenantId] = useState('')
  const [date,     setDate]     = useState(() => isoDay(new Date()))
  const [board,    setBoard]    = useState(null)
  const [pages,    setPages]    = useState([])
  const [loading,  setLoading]  = useState(true)
  const [busy,     setBusy]     = useState(null)
  const [error,    setError]    = useState(null)
  const [notice,   setNotice]   = useState(null)

  // The visitor picked from the pending list, waiting for a destination to be clicked.
  // Two clicks rather than a dropdown per row: "this driver, that ramp" is how the job
  // is actually described, and it keeps the grid readable.
  const [picked, setPicked] = useState(null)

  // A full exclusive destination that staff tried to assign to — holds the server's
  // 409 so the occupant can be shown and released in one step.
  const [conflict, setConflict] = useState(null)

  // null until a personal choice is made, so the tenant's default applies by itself.
  // Reading localStorage is wrapped: it throws in a private window and must not take
  // the page down over a view preference.
  const [viewOverride, setViewOverride] = useState(() => {
    try { return localStorage.getItem(VIEW_KEY) } catch { return null }
  })

  const chooseView = (next) => {
    setViewOverride(next)
    try { localStorage.setItem(VIEW_KEY, next) } catch { /* nothing lost but the memory */ }
  }

  const view = viewOverride ?? board?.defaultView ?? 'board'

  // Opening hours keep the grid short, but an exceptional delivery has to be placeable
  // outside them — a night arrival, or a Saturday at a site that normally closes.
  const [showFullDay, setShowFullDay] = useState(false)

  const today = isoDay(new Date())
  const isToday = date === today
  // A past day is a record: the server sends released assignments for it, so the grid
  // can show where everyone was. Nothing on it is actionable, and its counts describe
  // the day rather than this moment.
  const isPast = date < today

  useEffect(() => {
    if (!isOwner) { setTenantId(admin?.tenant_id ?? ''); return }
    apiFetch('/api/admin/tenants')
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : []
        setTenants(list)
        setTenantId(prev => prev || list[0]?.id || '')
      })
      .catch(() => {})
  }, [isOwner, admin, apiFetch])

  const load = useCallback(async () => {
    if (!tenantId) { setLoading(false); return }
    try {
      const res = await apiFetch(`/api/admin/destination-board/${tenantId}?date=${date}`)
      if (res.ok) setBoard(await res.json())
      else if (res.status === 403) setError('Kein Zugriff auf diesen Mandanten.')
    } catch {
      /* transient — the next refresh catches up */
    } finally {
      setLoading(false)
    }
  }, [tenantId, date, apiFetch])

  // Info pages are needed only for the per-row "Seite zuweisen" fallback, so they are
  // fetched once per tenant rather than on every board refresh. A failure here is not
  // worth surfacing: it only means that one control has no options.
  useEffect(() => {
    if (!tenantId) return
    apiFetch(`/api/admin/info-pages/${tenantId}`)
      .then(r => (r.ok ? r.json() : []))
      .then(d => setPages(Array.isArray(d) ? d : []))
      .catch(() => setPages([]))
  }, [tenantId, apiFetch])

  useEffect(() => { setLoading(true); setPicked(null); load() }, [load])

  useEffect(() => {
    const id = setInterval(load, REFRESH_MS)
    return () => clearInterval(id)
  }, [load])

  // SSE makes another station's assignment appear immediately; the interval above is
  // only the floor in case an event is missed.
  const esRef = useRef(null)
  useEffect(() => {
    if (!account || !tenantId) return
    let active = true
    let retry = null

    async function connect() {
      if (!active) return
      let token
      try { token = await getToken() } catch { retry = setTimeout(connect, 5000); return }
      if (!active) return

      const es = new EventSource(`${BACKEND_URL}/api/admin/events?token=${token}`)
      esRef.current = es
      const refresh = () => load()
      es.addEventListener('destination-assigned', refresh)
      es.addEventListener('destination-released', refresh)
      es.addEventListener('session-started', refresh)
      es.addEventListener('session-awaiting-assignment', refresh)
      es.addEventListener('session-assigned', refresh)
      es.addEventListener('session-refused', refresh)
      es.onerror = () => { es.close(); if (active) retry = setTimeout(connect, 5000) }
    }
    connect()

    return () => {
      active = false
      if (retry) clearTimeout(retry)
      esRef.current?.close()
    }
  }, [account, tenantId, getToken, load])

  const destinations = board?.destinations ?? []
  const pending      = board?.pending ?? []
  const onSite       = board?.onSite ?? []
  const abandoned    = board?.abandoned ?? []
  const slotMinutes  = board?.slotMinutes ?? 60
  // The day's opening window. A holiday closed all day comes as { closed, holiday }:
  // there is no window then, and the grid shows the full day with a note.
  const openHours    = board?.openHours?.closed ? null : (board?.openHours ?? null)
  const holidayName  = board?.openHours?.holiday ?? null

  /**
   * Which rows the grid shows.
   *
   * The range comes from the site's own opening hours — the standby window already
   * configured for the barrier logic — rather than a guess, so a yard working 06:00 to
   * 18:00 does not scroll through twelve empty night rows. Where no standby window is
   * configured the site runs around the clock, and the grid shows all 24 hours.
   *
   * An earlier version hardcoded 06:00–18:00 and widened it only to fit assignments
   * that already existed. That could not work: with no row outside the window, there
   * was no way to CREATE the first assignment outside it either. Hence both the real
   * opening hours and the manual override below.
   */
  const slots = useMemo(() => {
    const open = showFullDay ? null : openHours

    let start = open ? hhmmToMinutes(open.start) : 0
    let end   = open ? hhmmToMinutes(open.end)   : 24 * 60 - 1

    // A delivery booked outside opening hours must still be visible, so the range
    // stretches to include anything already placed, and "now" on today's view.
    const times = destinations
      .flatMap(d => d.visitors || [])
      .map(v => minutesOfDay(v.arrivalAt || v.assignedAt))
    if (isToday) times.push(new Date().getHours() * 60)

    for (const t of times) {
      if (Number.isFinite(t)) {
        start = Math.min(start, t)
        end   = Math.max(end, t)
      }
    }

    const first = Math.floor(start / slotMinutes) * slotMinutes
    const last  = Math.floor(end / slotMinutes) * slotMinutes

    const out = []
    for (let m = first; m <= last; m += slotMinutes) out.push(m)
    return out
  }, [destinations, slotMinutes, isToday, openHours, showFullDay])

  /** Whether a slot overlaps the day's lunch break — shaded, still usable. */
  const inBreak = (minutes) => {
    const brk = openHours?.break
    if (!brk) return false
    return minutes < hhmmToMinutes(brk.end) && minutes + slotMinutes > hhmmToMinutes(brk.start)
  }

  const slotLabel = (minutes) =>
    `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`

  /** Which slot an assignment belongs in — its stated arrival, else when it was made. */
  const slotFor = (visitor) =>
    Math.floor(minutesOfDay(visitor.arrivalAt || visitor.assignedAt) / slotMinutes) * slotMinutes

  /**
   * An ISO timestamp for a slot on the day being viewed.
   *
   * Built from the browser's own clock deliberately: the grid renders every row with
   * local hours, so a time chosen in a row has to come back in that same row. The
   * alternative — sending a naive 'YYYY-MM-DDTHH:MM' — would be read in the server
   * container's timezone (UTC) and the chip would reappear two rows off.
   */
  const slotToIso = (minutes) => {
    const pad = (n) => String(n).padStart(2, '0')
    const local = new Date(`${date}T${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}:00`)
    return Number.isNaN(local.getTime()) ? null : local.toISOString()
  }

  /**
   * Has this slot already finished?
   *
   * The server refuses an assignment into a slot that is over, so the cell should not
   * invite the click — a disabled cell explains itself, a 400 after the fact does not.
   * The slot containing the current moment stays open: somebody arriving at 14:20
   * belongs in the 14:00 row.
   *
   * Recomputed on every board refresh (the five-second poll), so the boundary moves
   * with the clock without a timer of its own.
   */
  const slotHasPassed = (minutes) => {
    if (date > today) return false       // a future day has no past slots
    if (date < today) return true        // a past day is entirely behind us
    const now = new Date()
    return minutes + slotMinutes <= now.getHours() * 60 + now.getMinutes()
  }

  /**
   * Assigns one visitor to one destination.
   *
   * Takes the visitor explicitly rather than reading the picked state, because the two
   * views choose them differently: the grid is pick-then-click (the time comes from
   * which cell), the queue picks straight from a row's dropdown. Same call underneath,
   * so neither view can develop its own idea of what assigning means.
   */
  const assignTo = async (visitor, destinationId, extra = {}) => {
    if (!visitor) return
    setBusy(extra.busyKey ?? destinationId); setError(null); setNotice(null)
    try {
      const res = await apiFetch(`/api/admin/destination-board/${tenantId}/assignments`, {
        method: 'POST',
        body: JSON.stringify({
          destinationId,
          sessionId: visitor.sessionId,
          visitDate: date,
          arrivalAt: extra.arrivalAt ?? null,
          releaseOccupantIds: extra.releaseOccupantIds,
        }),
      })
      const data = await res.json().catch(() => ({}))

      if (res.status === 409 && data.reason === 'full') {
        // Not an error so much as the moment a stale occupancy surfaces: it is noticed
        // precisely when the ramp is wanted. Offer to free it rather than refusing.
        // The visitor is kept so the retry knows who it was for.
        setConflict({ destinationId, visitor, ...data })
        return
      }
      if (!res.ok) throw new Error(data.error || 'Zuweisung fehlgeschlagen')

      if (data.movedFrom) setNotice(`${visitor.name || 'Besucher'} wurde umgesetzt — der vorherige Zielort ist wieder frei.`)
      setPicked(null); setConflict(null)
      await load()
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(null)
    }
  }

  /** The grid's form: the visitor comes from the pick-then-click state. */
  const assign = (destinationId, extra = {}) => assignTo(picked, destinationId, extra)

  const releaseAndReassign = async () => {
    if (!conflict) return
    const ids = (conflict.occupants || []).map(o => o.assignmentId)
    await assignTo(conflict.visitor ?? picked, conflict.destinationId, { releaseOccupantIds: ids })
  }

  /**
   * Assigns a bare info page instead of a destination.
   *
   * Kept for flows whose manual step answers with a page rather than a place — a
   * destination carries its own info page, so these are alternatives and a visitor
   * only ever gets one of them. Goes to the live-session endpoint because, unlike a
   * destination, this assignment lives only as long as the session does.
   */
  const assignPage = async (sessionId, pageId) => {
    if (!pageId) return
    setBusy(sessionId); setError(null); setNotice(null)
    try {
      const res = await apiFetch(`/api/admin/live-sessions/${tenantId}/${sessionId}`, {
        method: 'PATCH',
        body:   JSON.stringify({ pageId }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'Zuweisung fehlgeschlagen')
      if (data.alreadyCommunicated) {
        setNotice('Zugewiesen — der Besucher hatte aber bereits eine Seite gesehen und ist womöglich schon unterwegs.')
      }
      await load()
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(null)
    }
  }

  /**
   * The two answers to an overdue ramp that are not "release".
   *
   * "Noch belegt" is information rather than a snooze — the extension is counted, and
   * together with the eventual real release it is what makes a destination's default
   * duration a measured number. "Ich kümmere mich" stops the alerts and records who
   * took it, without freeing anything.
   */
  const act = async (assignmentId, path, body) => {
    setBusy(assignmentId); setError(null); setNotice(null)
    try {
      const res = await apiFetch(
        `/api/admin/destination-board/${tenantId}/assignments/${assignmentId}/${path}`,
        { method: 'PATCH', ...(body ? { body: JSON.stringify(body) } : {}) }
      )
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'Aktion fehlgeschlagen')
      await load()
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(null)
    }
  }

  const extend      = (assignmentId, minutes) => act(assignmentId, 'extend', { minutes })
  const acknowledge = (assignmentId)          => act(assignmentId, 'acknowledge')

  const release = async (assignmentId) => {
    setBusy(assignmentId); setError(null); setNotice(null)
    try {
      const res = await apiFetch(
        `/api/admin/destination-board/${tenantId}/assignments/${assignmentId}/release`,
        { method: 'PATCH' }
      )
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'Freigabe fehlgeschlagen')
      await load()
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(null)
    }
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Disposition</h1>
          <p>
            Wer ist an welchem Zielort, und welcher ist frei. Besucher auswählen und
            dann im Raster die Uhrzeit anklicken, zu der er erwartet wird.
          </p>
        </div>
      </div>

      <div className="card db-controls">
        {isOwner && tenants.length > 0 && (
          <div className="form-group db-control-group">
            <label>Mandant</label>
            <select className="form-control" value={tenantId} onChange={e => setTenantId(e.target.value)}>
              {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        )}

        <div className="form-group db-control-group">
          <label>Tag</label>
          <div className="db-day-picker">
            <button className="btn btn-ghost btn-sm" onClick={() => setDate(d => shiftDay(d, -1))}>‹</button>
            <input
              type="date"
              className="form-control"
              value={date}
              onChange={e => e.target.value && setDate(e.target.value)}
            />
            <button className="btn btn-ghost btn-sm" onClick={() => setDate(d => shiftDay(d, 1))}>›</button>
            {!isToday && (
              <button className="btn btn-ghost btn-sm" onClick={() => setDate(today)}>Heute</button>
            )}
          </div>
        </div>

        {/* Only meaningful in the grid, and only when opening hours actually shorten
            it — on a 24/7 site the grid already shows every hour. */}
        {view === 'board' && openHours && (
          <div className="form-group db-control-group">
            <label>Zeitraum</label>
            <div className="db-view-switch">
              <button
                className={`btn btn-sm${showFullDay ? ' btn-ghost' : ' btn-primary'}`}
                onClick={() => setShowFullDay(false)}
                title={`Öffnungszeiten ${openHours.start}–${openHours.end}`}
              >
                Öffnungszeiten
              </button>
              <button
                className={`btn btn-sm${showFullDay ? ' btn-primary' : ' btn-ghost'}`}
                onClick={() => setShowFullDay(true)}
                title="Alle 24 Stunden anzeigen, z. B. für eine Nachtanlieferung"
              >
                24 h
              </button>
            </div>
          </div>
        )}

        {holidayName && (
          <div className="form-group db-control-group">
            <label>Feiertag</label>
            <span className="badge badge-blue db-holiday">
              {holidayName}{board?.openHours?.closed ? ' — geschlossen' : ` — ${openHours?.start}–${openHours?.end}`}
            </span>
          </div>
        )}

        <div className="form-group db-control-group">
          <label>Ansicht</label>
          <div className="db-view-switch">
            <button
              className={`btn btn-sm${view === 'board' ? ' btn-primary' : ' btn-ghost'}`}
              onClick={() => chooseView('board')}
            >
              Tagesraster
            </button>
            <button
              className={`btn btn-sm${view === 'queue' ? ' btn-primary' : ' btn-ghost'}`}
              onClick={() => chooseView('queue')}
            >
              Warteschlange
            </button>
          </div>
        </div>
      </div>

      {/* Renders nothing unless the tenant may operate at least one barrier from here.
          A visitor picked for assignment is passed on, so opening the gate for them is
          logged against their session. */}
      <BarrierControls tenantId={tenantId} visitor={picked} />
      {/* Standby / Schwarz / Geöffnet for the tenant's kiosks — nothing without the feature. */}
      <KioskDisplayControls tenantId={tenantId} />

      {error  && <div className="alert alert-error">{error}</div>}
      {notice && <div className="alert alert-warning">{notice}</div>}

      {conflict && (
        <div className="alert alert-warning db-conflict">
          <div>
            <strong>Zielort belegt</strong> ({conflict.occupied} / {conflict.capacity})
            {(conflict.occupants || []).map(o => (
              <div key={o.assignmentId} className="db-conflict-occupant">
                {o.name || o.company || 'Unbekannt'}
                {o.licensePlate ? ` · ${o.licensePlate}` : ''}
                {o.assignedAt ? ` · seit ${hhmm(o.assignedAt)}` : ''}
              </div>
            ))}
          </div>
          <div className="db-conflict-actions">
            {conflict.canReleaseAndReassign && (
              <button className="btn btn-primary btn-sm" onClick={releaseAndReassign}>
                Freigeben und neu zuweisen
              </button>
            )}
            <button className="btn btn-ghost btn-sm" onClick={() => setConflict(null)}>Abbrechen</button>
          </div>
        </div>
      )}

      {view === 'queue' ? (
        <VisitorQueue
          queue={board?.queue ?? []}
          destinations={destinations}
          destinationsEnabled={!!board?.destinationsEnabled}
          showUnfinished={board?.showUnfinished !== false}
          // A past day is a record. Without this the dropdowns would still offer to
          // assign, and an assignment dated yesterday would be written at today's time.
          readOnly={isPast}
          pages={pages}
          busy={busy}
          onAssignDestination={(visitor, destinationId) =>
            assignTo(visitor, destinationId, { busyKey: visitor.sessionId })}
          onAssignPage={assignPage}
          onRelease={release}
        />
      ) : (
      <>
      <div className="db-layout">
        {/* ── Who needs a destination ── */}
        <div className="card db-pending">
          <div className="section-title db-title">
            Wartet auf Zuweisung
            <span className="db-badge">{pending.length}</span>
          </div>

          {loading ? (
            <div className="loading-state"><span className="spinner" /></div>
          ) : pending.length === 0 ? (
            <p className="db-empty">Alle Besucher dieses Tages haben einen Zielort.</p>
          ) : (
            <div className="db-pending-list">
              {pending.map(p => (
                <div
                  key={p.sessionId}
                  className={`db-pending-item${p.awaitingSince ? ' db-pending-item--waiting' : ''}`}
                >
                  <button
                    className={`db-pending-row${picked?.sessionId === p.sessionId ? ' db-pending-row--picked' : ''}`}
                    onClick={() => { setPicked(picked?.sessionId === p.sessionId ? null : p); setConflict(null) }}
                  >
                    <span className="db-pending-name">{p.name || 'Unbekannt'}</span>
                    <span className="db-pending-meta">
                      {p.company && <span>{p.company}</span>}
                      {p.licensePlate && <span className="db-plate">{p.licensePlate}</span>}
                      {/* Still in the flow: the one worth assigning now, while they are
                          working through safety rules and the answer can still reach them. */}
                      {p.origin === 'in_flow' && <span className="db-inflow">im Ablauf</span>}
                      {/* Standing on the step right now — the only urgent state here. */}
                      {p.awaitingSince && (
                        <span className="db-waiting">
                          <Elapsed since={p.awaitingSince} prefix="wartet seit" />
                        </span>
                      )}
                    </span>
                  </button>

                  {/* Only for flows that answer with a page rather than a place. A
                      destination brings its own page, so the two never both apply. */}
                  {p.pageAssignable && pages.length > 0 && (
                    <select
                      className="form-control db-page-select"
                      value={p.assignedPageId ?? ''}
                      disabled={busy === p.sessionId}
                      onChange={e => assignPage(p.sessionId, e.target.value)}
                    >
                      <option value="">— Seite zuweisen —</option>
                      {pages.map(pg => <option key={pg.id} value={pg.id}>{pg.name}</option>)}
                    </select>
                  )}
                </div>
              ))}
            </div>
          )}

          {picked && (
            <p className="db-hint">
              <strong>{picked.name || 'Besucher'}</strong> ausgewählt — jetzt im Raster
              eine Uhrzeit anklicken. {isToday
                ? 'Über die Spaltenüberschrift ohne Uhrzeit („jetzt“).'
                : 'An einem Planungstag ist die Uhrzeit erforderlich.'}
            </p>
          )}
        </div>

        {/* ── The day grid ── */}
        <div className="card db-grid-card">
          <div className="section-title db-title">
            Zielorte
            <span className="db-badge">{destinations.length}</span>
            {!isToday && <span className="db-badge db-badge--planning">Planung</span>}
          </div>

          {loading ? (
            <div className="loading-state"><span className="spinner" /></div>
          ) : !board?.destinationsEnabled ? (
            // A tenant that only assigns info pages manually: the grid would have
            // nothing to show, so it says so rather than rendering an empty table.
            <p className="db-empty">
              Für diesen Mandanten sind keine Zielorte aktiviert. Besucher erhalten hier
              eine Informationsseite; eine Rampenübersicht lässt sich in den Funktionen
              aktivieren.
            </p>
          ) : destinations.length === 0 ? (
            <p className="db-empty">
              Noch keine Zielorte angelegt. Rampen und Wartebereiche werden unter
              „Zielorte" in den Einstellungen gepflegt.
            </p>
          ) : (
            <div className="db-grid-scroll">
              <table className="db-grid">
                <thead>
                  <tr>
                    <th className="db-time-col">Zeit</th>
                    {destinations.map(d => (
                      <th key={d.id} className={d.full ? 'db-col-full' : ''}>
                        {/* The header assigns without a stated time, which only makes
                            sense for today — the visitor is here now. On a planning day
                            there is no "now" to use, so a slot must be clicked instead
                            and the header is inert. */}
                        <button
                          className="db-dest-head"
                          disabled={!picked || !isToday || busy === d.id}
                          onClick={() => assign(d.id)}
                          title={
                            !picked ? 'Erst einen Besucher auswählen'
                            : !isToday ? 'Beim Planen eine Uhrzeit im Raster anklicken'
                            : `${picked.name || 'Besucher'} jetzt hierhin zuweisen`
                          }
                        >
                          <span className="db-dest-name">{d.displayName}</span>
                          <span className="db-dest-state">
                            {/* On a past day: how many were here, because "now" and
                                "capacity" mean nothing then. Otherwise who is there NOW
                                against capacity — anything booked for a later hour is
                                counted separately, since together they read as "3/1" on
                                an ordinary planning day, which looks like an overbooked
                                ramp rather than three trucks across a day. */}
                            {isPast
                              ? `${d.occupied} an diesem Tag`
                              : <>
                                  {d.occupiedNow ?? d.occupied}{d.capacity != null ? `/${d.capacity}` : '/∞'}
                                  {d.assignment_mode === 'shared' && <span className="db-shared"> geteilt</span>}
                                </>}
                          </span>
                          {!isPast && d.occupied > (d.occupiedNow ?? d.occupied) && (
                            <span className="db-planned" title="Für später am Tag eingeplant">
                              {d.occupied - (d.occupiedNow ?? d.occupied)} geplant
                            </span>
                          )}
                          {d.pressure === 'warn' && <span className="db-pressure">fast voll</span>}
                          {d.pressure === 'full' && <span className="db-pressure db-pressure--full">voll</span>}
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {slots.map(minutes => (
                    <tr key={minutes} className={inBreak(minutes) ? 'db-row-break' : undefined}>
                      <td className="db-time-col" title={inBreak(minutes) ? 'Mittagspause' : undefined}>
                        {slotLabel(minutes)}
                      </td>
                      {destinations.map(d => {
                        const here = (d.visitors || []).filter(v => slotFor(v) === minutes)
                        return (
                          <td key={d.id} className="db-cell">
                            {/* The cell IS the control: clicking it assigns the picked
                                visitor to this destination AT this time. Without it the
                                only target was the column, which meant every assignment
                                landed at the current hour — wrong for anything planned
                                for another day. */}
                            {picked && (
                              <button
                                className="db-cell-target"
                                disabled={busy === d.id || slotHasPassed(minutes)}
                                onClick={() => assign(d.id, { arrivalAt: slotToIso(minutes) })}
                                title={slotHasPassed(minutes)
                                  ? 'Diese Uhrzeit ist bereits vorbei'
                                  : `${picked.name || 'Besucher'} → ${d.displayName}, ${slotLabel(minutes)}`}
                              >
                                {slotLabel(minutes)}
                              </button>
                            )}
                            {here.map(v => (
                              <div
                                key={v.assignmentId}
                                className={`db-chip${v.reserved ? ' db-chip--reserved' : ''}${v.carriedOver ? ' db-chip--carried' : ''}${isOverdue(v) ? ' db-chip--overdue' : ''}${v.acknowledgedAt ? ' db-chip--acknowledged' : ''}${v.released ? ' db-chip--released' : ''}`}
                              >
                                {/* Clicking a placed visitor picks them up again, which
                                    is how a time or destination gets corrected — they
                                    are no longer in the pending list to re-select. Not
                                    on a closed assignment: there is nothing left to
                                    correct about a day that is over. */}
                                {v.released ? (
                                  <span className="db-chip-name">
                                    {v.name || v.company || 'Unbekannt'}
                                  </span>
                                ) : (
                                  <button
                                    className="db-chip-name db-chip-pick"
                                    onClick={() => {
                                      setPicked(picked?.sessionId === v.sessionId
                                        ? null
                                        : { sessionId: v.sessionId, name: v.name || v.company })
                                      setConflict(null)
                                    }}
                                    title="Auswählen, um Zeit oder Zielort zu ändern"
                                  >
                                    {v.name || v.company || 'Unbekannt'}
                                  </button>
                                )}
                                <span className="db-chip-meta">
                                  {v.licensePlate && <span className="db-plate">{v.licensePlate}</span>}
                                  {v.reserved && <span>vorgemerkt</span>}
                                  {v.carriedOver && <span>aus {v.visitDate}</span>}
                                  {v.source === 'rule' && <span>per Regel</span>}
                                  {/* Assigned mid-flow: their check-in is not finished
                                      yet, which is why there is no company here. */}
                                  {v.fromLiveSession && <span className="db-inflow">im Ablauf</span>}
                                  {/* Assigned mid-flow and the visitor never finished:
                                      there is no name because there is no check-in,
                                      and most likely nobody at the ramp. Said plainly
                                      rather than left as a bare "Unbekannt", which
                                      reads as missing data instead of a stale hold. */}
                                  {board?.showUnfinished !== false && !v.hasCheckin && !v.fromLiveSession && (
                                    <span
                                      className="db-stale-hold"
                                      title={'Der Check-in wurde nie abgeschlossen. Das heißt nicht zwingend, '
                                           + 'dass niemand da ist — wurde das Tor nach einem Anruf von Hand '
                                           + 'geöffnet, steht der Fahrer dort. Bitte prüfen und ggf. freigeben.'}
                                    >
                                      ohne Check-in — prüfen
                                    </span>
                                  )}
                                  {v.acknowledgedAt && (
                                    <span title={`Übernommen von ${v.acknowledgedBy || 'unbekannt'}`}>
                                      übernommen
                                    </span>
                                  )}
                                  {v.extensionCount > 0 && <span>+{v.extensionCount}×</span>}
                                  {/* When it was given up, which on a historical day is
                                      the one thing the chip cannot show as elapsed time. */}
                                  {v.released && v.releasedAt && (
                                    <span title={`Freigegeben von ${v.releasedBy || 'unbekannt'}`}>
                                      bis {new Date(v.releasedAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                  )}
                                </span>

                                {/* The three answers, inline. An alert that sends you
                                    looking for another page to act on is half an alert.
                                    None of them apply to a closed assignment. */}
                                {!v.released && isOverdue(v) && !v.acknowledgedAt && (
                                  <>
                                    <button
                                      className="db-chip-action"
                                      disabled={busy === v.assignmentId}
                                      onClick={() => extend(v.assignmentId, 30)}
                                      title="Noch belegt — 30 Minuten verlängern"
                                    >
                                      +30
                                    </button>
                                    <button
                                      className="db-chip-action"
                                      disabled={busy === v.assignmentId}
                                      onClick={() => acknowledge(v.assignmentId)}
                                      title="Ich kümmere mich — keine weiteren Meldungen"
                                    >
                                      ✓
                                    </button>
                                  </>
                                )}

                                {!v.released && (
                                  <button
                                    className="db-chip-release"
                                    disabled={busy === v.assignmentId}
                                    onClick={() => release(v.assignmentId)}
                                    title="Zielort freigeben"
                                  >
                                    ×
                                  </button>
                                )}
                              </div>
                            ))}
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Freed because nobody arrived ──
          Collapsed by default: it is a record, not something to act on. Visible at all
          so a ramp becoming free again has a visible reason, and so the one-hour grace
          period can be judged against what actually happens. */}
      {isToday && abandoned.length > 0 && (
        <details className="card db-abandoned">
          <summary className="section-title db-title">
            Abgebrochen
            <span className="db-badge">{abandoned.length}</span>
            <span className="db-abandoned-note">ohne Check-in — Zielort wieder frei</span>
          </summary>
          <div className="db-onsite-list">
            {abandoned.map(a => (
              <div key={a.assignmentId} className="db-onsite-row">
                <div className="db-onsite-who">
                  <span className="db-onsite-name">{a.name || 'Unbekannt'}</span>
                  <span className="db-pending-meta">
                    {a.company && <span>{a.company}</span>}
                    {a.licensePlate && <span className="db-plate">{a.licensePlate}</span>}
                  </span>
                </div>
                <div className="db-onsite-where">
                  <span className="db-onsite-dest">{a.destination}</span>
                  <span className="db-onsite-src">
                    zugewiesen {hhmm(a.assignedAt)}
                    {a.assignedBy ? ` · ${a.assignedBy}` : ''}
                  </span>
                </div>
                <span className="db-onsite-since">freigegeben {hhmm(a.releasedAt)}</span>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* ── Who is on site ──
          Kept until the visitor checks out rather than until their flow finishes:
          "which ramp is that driver at?" is a question that outlives the check-in by
          hours, and answering it is why this page exists. Shown for today only — on a
          planning day it would describe the wrong people. */}
      {isToday && (
        <div className="card db-onsite">
          <div className="section-title db-title">
            Vor Ort
            <span className="db-badge">{onSite.length}</span>
          </div>

          {loading ? (
            <div className="loading-state"><span className="spinner" /></div>
          ) : onSite.length === 0 ? (
            <p className="db-empty">
              Niemand eingecheckt. Eingecheckte Besucher bleiben hier sichtbar, bis sie
              auschecken.
            </p>
          ) : (
            <div className="db-onsite-list">
              {onSite.map(s => (
                <div key={s.sessionId} className="db-onsite-row">
                  <div className="db-onsite-who">
                    <span className="db-onsite-name">{s.name || 'Unbekannt'}</span>
                    <span className="db-pending-meta">
                      {s.licensePlate && <span className="db-plate">{s.licensePlate}</span>}
                      {s.flowMode && <span className="db-flow">{s.flowMode}</span>}
                    </span>
                  </div>

                  {/* Destination first when there is one, since it is the physical
                      answer; otherwise the info page they were shown. Both are
                      populated for rule-based flows too, so nobody has to read the
                      server log to find out where someone went. */}
                  <div className="db-onsite-where">
                    {s.destination
                      ? <span className="db-onsite-dest">→ {s.destination}</span>
                      : s.shownPageName
                        ? <>
                            <span className="db-onsite-dest">→ {s.shownPageName}</span>
                            <span className="db-onsite-src">{describeSource(s)}</span>
                          </>
                        : <span className="db-onsite-none">kein Ziel zugewiesen</span>}
                  </div>

                  <span className="db-onsite-since">
                    eingecheckt <Elapsed since={s.checkedInAt} prefix="seit" />
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      </>
      )}
    </div>
  )
}

/**
 * How the shown page was chosen, in admin-facing wording. The rule id is included
 * because with several rules "which one fired?" is the actual question.
 */
function describeSource(s) {
  if (s.shownSource === 'manual')   return 'manuell zugewiesen'
  if (s.shownSource === 'rule')     return `Regel${s.shownRuleId ? ` ${s.shownRuleId}` : ''}`
  if (s.shownSource === 'fallback') return 'Rückfallseite'
  return ''
}

/**
 * Live-ticking elapsed time. Counts seconds under an hour, because that is the case
 * where someone is standing at the terminal and the seconds are the urgency; past an
 * hour it switches to "1 h 23 min", since "287:14" has to be divided in your head.
 * Same wording as the daily report's dwell times.
 */
function Elapsed({ since, prefix }) {
  const [, tick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => tick(n => n + 1), 1000)
    return () => clearInterval(id)
  }, [])

  if (!since) return null
  const secs = Math.max(0, Math.floor((Date.now() - new Date(since).getTime()) / 1000))

  if (secs < 3600) {
    return <>{prefix} {Math.floor(secs / 60)}:{String(secs % 60).padStart(2, '0')}</>
  }

  const hours   = Math.floor(secs / 3600)
  const minutes = Math.floor((secs % 3600) / 60)
  return <>{prefix} {hours} h{minutes ? ` ${minutes} min` : ''}</>
}
